import { motion } from 'motion/react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Container, Kicker, Mark } from '@/components/primitives';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FeatureGrid } from '@/components/FeatureGrid';
import { FeatureVisual } from '@/components/FeatureVisuals';
import { CTA } from '@/components/CTA';
import { trovaFunzionalita } from '@/content/funzionalita';

export default function FunzionalitaPage() {
  const { slug } = useParams();
  const f = trovaFunzionalita(slug);
  if (!f) return <Navigate to="/prodotto" replace />;

  let sezione = 0;
  const prossimo = () => String(++sezione).padStart(2, '0');

  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(50% 50% at 50% -10%, color-mix(in oklch, var(--primary) 18%, transparent), transparent 70%)',
          }}
        />
        <Container className="relative">
          <Badge variant="soft">· Funzionalità {f.code} · {f.name}</Badge>
          <motion.h1
            key={f.slug}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="boss-display mt-6 max-w-[18ch] text-balance text-[52px] md:text-[96px]"
          >
            {f.title} <Mark>{f.mark}</Mark>
          </motion.h1>
          <p className="mt-7 max-w-2xl text-pretty text-[18px] leading-[1.55] text-fg-2">{f.lead}</p>
          <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Per chi · {f.perChi}
          </div>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button asChild size="lg">
              <a href="#demo">Prenota una demo →</a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/prodotto">Tutte le funzionalità</Link>
            </Button>
          </div>

          <div className="mt-16 md:mt-20">
            <FeatureVisual key={f.slug} slug={f.slug} />
            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              fig. — {f.name} in BOSS · dati di esempio
            </div>
          </div>
        </Container>
      </section>

      {/* Il problema */}
      <section className="relative border-t border-border py-24 md:py-32">
        <Container>
          <Kicker index={prossimo()} label="Il problema" />
          <h2 className="boss-display mt-5 max-w-4xl text-balance text-[36px] md:text-[56px]">
            {f.problema.title}
          </h2>
          <p className="mt-6 max-w-2xl text-pretty text-[16px] leading-[1.6] text-fg-2">{f.problema.copy}</p>
        </Container>
      </section>

      {/* Cosa fa */}
      <section className="relative border-t border-border py-24 md:py-32">
        <Container>
          <Kicker index={prossimo()} label="Cosa fa BOSS" />
          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
            {f.capacita.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="bg-background p-7"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-[18px] font-light tracking-tight text-foreground">{c.title}</h3>
                  {c.inArrivo && <Badge variant="outline">In arrivo</Badge>}
                </div>
                <p className="mt-3 text-pretty text-[14px] leading-[1.6] text-fg-2">{c.copy}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Come funziona */}
      {f.passi && (
        <section className="relative border-t border-border py-24 md:py-32">
          <Container>
            <Kicker index={prossimo()} label="Come funziona" />
            <ol className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
              {f.passi.map((p, i) => (
                <motion.li
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-input font-mono text-[10px] text-primary">
                    0{i + 1}
                  </span>
                  <h3 className="boss-h1 mt-5 text-balance text-[26px]">{p.title}</h3>
                  <p className="mt-3 text-pretty text-[14px] leading-[1.6] text-fg-2">{p.copy}</p>
                </motion.li>
              ))}
            </ol>
          </Container>
        </section>
      )}

      {/* Numeri */}
      {f.numeri && (
        <section className="relative border-t border-border py-24 md:py-32">
          <Container>
            <Kicker index={prossimo()} label="In numeri" />
            <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-2 lg:grid-cols-4">
              {f.numeri.map((n) => (
                <div key={n.unit} className="flex flex-col bg-background p-7">
                  <span className="text-[64px] font-light leading-none tracking-tight tabular-nums">{n.figure}</span>
                  <div className="mt-2 text-[14px] leading-tight text-primary">{n.unit}</div>
                  <p className="mt-5 text-pretty text-[13px] leading-[1.55] text-fg-2">{n.label}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      <section className="relative border-t border-border py-20 md:py-28">
        <Container>
          <blockquote className="boss-display max-w-4xl text-balance text-[32px] md:text-[48px]">
            <span className="text-primary">“</span>
            {f.pull}
            <span className="text-primary">”</span>
          </blockquote>
        </Container>
      </section>

      <FeatureGrid index={prossimo()} escludi={f.slug} compact />
      <CTA />
    </>
  );
}
